import React, { useRef, useState } from 'react';

import { IconScout } from '@/components/ui/images/IconScout';
import type { IconScoutName } from '@/components/ui/images/IconScout';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import type { MonthlyCutConceptsStatus } from '@/api/domains/attendance/types';


interface ConceptsStatusAppearance {
  icon: IconScoutName;
  color: string;
  tooltipKey: string;
}

// Íconos por estado según el diseño (Figma "Cortes mensuales", nodo 110:6576). Cada estado es un
// único glifo circular del set Line de Unicons: es el que está instalado en el proyecto y equivale
// a los sólidos del diseño. "No generados" y "error de envío" comparten glifo (times-circle) y se
// distinguen solo por color.
const APPEARANCE_BY_STATUS: Record<MonthlyCutConceptsStatus, ConceptsStatusAppearance> = {
  NOT_GENERATED: {
    icon: 'timesCircle',
    color: colors.primary.dark,
    tooltipKey: 'attendance:monthlyCutsList.concepts.tooltip.notGenerated',
  },
  GENERATED: {
    icon: 'checkCircle',
    color: colors.primary.dark,
    tooltipKey: 'attendance:monthlyCutsList.concepts.tooltip.generated',
  },
  PARTIALLY_SENT: {
    icon: 'exclamationCircle',
    color: colors.feedback.alert300,
    tooltipKey: 'attendance:monthlyCutsList.concepts.tooltip.partiallySent',
  },
  SENT: {
    icon: 'arrowCircleUp',
    color: colors.important.main,
    tooltipKey: 'attendance:monthlyCutsList.concepts.tooltip.sent',
  },
  SEND_ERROR: {
    icon: 'timesCircle',
    color: colors.feedback.error300,
    tooltipKey: 'attendance:monthlyCutsList.concepts.tooltip.sendError',
  },
};

const ICON_SIZE = 24;

export interface MonthlyCutConceptsCellProps {
  status: MonthlyCutConceptsStatus;
  // Navegación a Conceptos de pago con los filtros del corte preaplicados. Ausente cuando el
  // usuario no tiene lectura en esa pantalla: el ícono se muestra igual, pero no es interactivo.
  onNavigate?: () => void;
}

/**
 * Celda de la columna "Conceptos" del listado de cortes mensuales (ASIS-5435): un ícono por estado
 * del ciclo de conceptos, con tooltip en hover y en foco por teclado. Los cuatro estados con
 * conceptos ya generados navegan a la pantalla de Conceptos de pago; "no generados" no es
 * interactivo, según el criterio de aceptación.
 */
export const MonthlyCutConceptsCell: React.FC<MonthlyCutConceptsCellProps> = ({
  status,
  onNavigate,
}) => {
  const { t: translate } = useTranslation();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Resguardo ante un código no reconocido (una versión de API más nueva, o un valor nulo): sin él
  // la celda rompería y con ella la fila completa. Se normaliza al estado neutro para que también
  // deje de ser interactiva, igual que "no generados". Espeja el fallback del backend.
  const resolvedStatus: MonthlyCutConceptsStatus =
    status in APPEARANCE_BY_STATUS ? status : 'NOT_GENERATED';
  const appearance = APPEARANCE_BY_STATUS[resolvedStatus];
  const tooltipText = translate(appearance.tooltipKey);
  const isInteractive = resolvedStatus !== 'NOT_GENERATED' && onNavigate !== undefined;

  const glyph = <IconScout name={appearance.icon} size={ICON_SIZE} color={appearance.color} />;

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      data-testid="monthly-cut-concepts-cell"
      data-status={resolvedStatus}
    >
      {isInteractive ? (
        <button
          type="button"
          // `p-0 border-0 bg-transparent leading-none` resetea los defaults del navegador para el
          // botón: sin eso el ícono queda desalineado frente a las demás celdas centradas.
          className="inline-flex items-center justify-center p-0 border-0 bg-transparent leading-none cursor-pointer"
          aria-label={tooltipText}
          onClick={onNavigate}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          data-testid="monthly-cut-concepts-button"
        >
          {glyph}
        </button>
      ) : (
        <span
          className="inline-flex items-center justify-center cursor-help"
          tabIndex={0}
          role="img"
          aria-label={tooltipText}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          data-testid="monthly-cut-concepts-icon"
        >
          {glyph}
        </span>
      )}

      {showTooltip && (
        <Tooltip
          body={tooltipText}
          anchorRef={anchorRef}
          strategy="absolute"
          placement="top"
          maxWidth={280}
        />
      )}
    </span>
  );
};

export default MonthlyCutConceptsCell;
